import React, { memo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Popover, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import clsx from "clsx";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";

import {
  APPLY_VOLUME_OVERLAY,
  SELECT_VOLUME_OVERLAY,
} from "../../consts/sentryIds";
import { COLLECTIONS_PLAN_TYPE } from "../../consts/planTypes";
import { NONE_SELECTED } from "../../consts/strings";
import { VOLUME_OVERLAY_TABLE } from "../../consts/routes";
import { palette } from "../../theme/palette";

import { fetchSimulationPercentages } from "../../reducers/matrixProfile/thunkActions";
import { simulatedOverlayOpenSelector } from "../../reducers/ownApp/selectors";
import { mapEditModeSelector } from "../../reducers/depot/selectors";
import {
  overlaysSelector,
  selectedOverlaySelector,
  getMatrixProfileType,
  isEditOneModeSelector,
  isWaveEditModeSelector,
} from "../../reducers/matrixProfile/selectors";
import {
  setHeatmapView,
  setSelectedOverlay,
} from "../../reducers/matrixProfile";

import { View } from "../Base";
import { SelectSimulatedOverlay } from "../Modals/SelectSimulatedOverlay/SelectSimulatedOverlay";
import { DatePickerCalendar } from "../DatePickerCalendar/DatePickerCalendar";

const useStyles = makeStyles({
  container: {
    marginLeft: "20px",
    alignItems: "center",
    display: "flex",
  },
  header: {
    backgroundColor: "#303030",
    padding: "19px 18px 10px 18px",
  },
  componentTitle: {
    color: palette.typography.white.high,
    fontSize: "12px",
  },
  selectedValueText: {
    color: palette.typography.white.high,
    fontSize: "24px",
    marginTop: "5px",
  },
  datePickerContainer: {
    padding: "15px",
  },
  text: {
    color: palette.typography.white.high,
    fontSize: "12px",
    marginRight: "4px",
  },
  valueContainer: {
    display: "flex",
    color: palette.typography.white.high,
    cursor: "pointer",
  },
  icon: {
    width: "20px",
    height: "19px",
    margin: 0,
    color: palette.typography.white.high,
    transform: "rotate(90deg)",
  },
  breakdownButton: {
    color: "#303030",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    marginLeft: "10px",
    marginRight: "auto",
  },
  actionButton: {
    color: "#303030",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    marginLeft: "20px",
    marginRight: "10px",
  },
  disabledButton: {
    opacity: 0.6,
    cursor: "default",
  },
  buttonsContainer: {
    marginTop: "20px",
  },
  disabled: {
    color: palette.typography.black.disabled,
    cursor: "default",
  },
  disabledCursor: {
    cursor: "default",
  },
});

export const VolumeOverlay = memo(() => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [date, setDate] = useState();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const overlays = useSelector(overlaysSelector);
  const selectedOverlay = useSelector(selectedOverlaySelector);
  const planType = useSelector(getMatrixProfileType);
  const mapEditMode = useSelector(mapEditModeSelector);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const isWaveEditMode = useSelector(isWaveEditModeSelector);
  const selectSimulatedOverlayOpen = useSelector(simulatedOverlayOpenSelector);

  const isDisabled =
    COLLECTIONS_PLAN_TYPE === planType ||
    isEditOneMode ||
    isWaveEditMode ||
    mapEditMode;

  const handleClick = (event) => {
    if (isDisabled) return;
    setDate(
      selectedOverlay ? moment(selectedOverlay.overlayDate).toDate() : null
    );
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const getOverlayByDate = useCallback(
    // Added percentage as a parameter because the simulated percentage modal doesn't re-run fetchVolumeData as the selectOverlay function only checks for a change in overlay date, not percentage
    // But as getOverlayByDate is used in filterDate it won't get all the correct dates if you put overlay.simulationPercentage = currentPercentage
    (date, percentage = 0) =>
      (overlays || []).find(
        (overlay) =>
          moment(date).tz("Europe/London").format("YYYY-MM-DD") ===
            moment(overlay.overlayDate).format("YYYY-MM-DD") &&
          percentage === overlay.simulationPercentage
      ),
    [overlays]
  );

  const filterDate = useCallback(
    (date) => !!getOverlayByDate(date, 0),
    [getOverlayByDate]
  );

  const onDateChanged = (date) =>
    getOverlayByDate(date, 0) ? setDate(date) : setDate(null);

  const checkSimulationPercentages = async () => {
    dispatch(fetchSimulationPercentages({ overlayDate: date, selectOverlay }));
  };

  const selectOverlay = (currentSimulationPercentage = 0) => {
    // Added percentage parameter because the dispatch for updating the Percentage is called just before selectOverlay so it doesn't update in time
    const overlay = getOverlayByDate(date, currentSimulationPercentage);

    if (overlay && overlay !== selectedOverlay) {
      dispatch(setSelectedOverlay(overlay));
    }
    setAnchorEl(null);
  };

  const clearOverlay = () => {
    if (isDisabled) return;
    dispatch(setSelectedOverlay(null));
    dispatch(setHeatmapView(false));
  };

  const formatDateWithOffset = (date) => {
    return date
      ? moment(date).tz("Europe/London").format("ddd, DD MMMM")
      : NONE_SELECTED;
  };

  const goToBreakdown = () => {
    if (!date) return;
    selectOverlay(0);
    navigate(VOLUME_OVERLAY_TABLE);
  };

  return (
    <View className={styles.container} sentryId={SELECT_VOLUME_OVERLAY}>
      <SelectSimulatedOverlay
        open={selectSimulatedOverlayOpen}
        selectOverlay={selectOverlay}
      />
      <Typography className={styles.text}>Volume Overlay:</Typography>
      <View
        className={clsx(
          styles.valueContainer,
          isDisabled && styles.disabledCursor
        )}
      >
        <Typography className={styles.text} onClick={handleClick}>
          {selectedOverlay
            ? moment(selectedOverlay.overlayDate).format("DD/MM/YY")
            : NONE_SELECTED}
        </Typography>
        {selectedOverlay && (
          <ClearIcon
            className={clsx(styles.icon, isDisabled && styles.disabled)}
            onClick={clearOverlay}
          />
        )}
        <ChevronRightIcon
          className={clsx(styles.icon, isDisabled && styles.disabled)}
          onClick={handleClick}
        />
      </View>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <View className={styles.header}>
          <Typography className={styles.componentTitle}>
            SELECT VOLUME OVERLAY DATE
          </Typography>
          <Typography className={styles.selectedValueText}>
            {formatDateWithOffset(date)}
          </Typography>
        </View>

        <View className={styles.datePickerContainer}>
          <DatePickerCalendar
            pastDates
            filterDate={filterDate}
            onChange={onDateChanged}
            date={date}
          />

          <Grid
            container
            justifyContent={"flex-end"}
            direction={"row"}
            className={styles.buttonsContainer}
          >
            {location.pathname !== VOLUME_OVERLAY_TABLE && (
              <Typography
                className={clsx(
                  styles.breakdownButton,
                  !date && styles.disabled
                )}
                onClick={goToBreakdown}
              >
                VOLUME BREAKDOWN
              </Typography>
            )}
            <Typography className={styles.actionButton} onClick={handleClose}>
              CANCEL
            </Typography>
            <Typography
              className={clsx(styles.actionButton, !date && styles.disabled)}
              onClick={checkSimulationPercentages}
              sentryId={APPLY_VOLUME_OVERLAY}
            >
              OK
            </Typography>
          </Grid>
        </View>
      </Popover>
    </View>
  );
});

export default VolumeOverlay;
